import React, { createContext, useContext, useMemo, useState } from 'react';
import { cn } from '../../lib/cn';

type CommandContextValue = {
  search: string;
  setSearch: (value: string) => void;
};

const CommandContext = createContext<CommandContextValue>({ search: '', setSearch: () => {} });

export function Command({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  const [search, setSearch] = useState('');
  return (
    <CommandContext.Provider value={{ search, setSearch }}>
      <div className={cn('flex flex-col', className)}>{children}</div>
    </CommandContext.Provider>
  );
}

export function CommandInput({
  className,
  placeholder,
  ...props
}: React.InputHTMLAttributes<HTMLInputElement>) {
  const { search, setSearch } = useContext(CommandContext);
  return (
    <input
      value={search}
      onChange={(event) => setSearch(event.target.value)}
      placeholder={placeholder}
      className={cn(
        'border-b border-[var(--cream-2)] bg-transparent px-3 py-2 text-sm outline-none',
        className,
      )}
      {...props}
    />
  );
}

export function CommandList({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return <div className={cn('max-h-60 overflow-auto p-1', className)}>{children}</div>;
}

export function CommandEmpty({ children }: { children: React.ReactNode }) {
  return <div className="px-3 py-4 text-center text-sm text-[var(--ink-3)]">{children}</div>;
}

export function CommandGroup({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return <div className={cn('p-1', className)}>{children}</div>;
}

export function CommandItem({
  children,
  value = '',
  onSelect,
  className,
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement> & {
  value?: string;
  onSelect?: () => void;
}) {
  const { search } = useContext(CommandContext);
  const haystack = `${value} ${typeof children === 'string' ? children : ''}`.toLowerCase();
  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return !q || haystack.includes(q);
  }, [haystack, search]);

  if (!visible) return null;

  return (
    <button
      type="button"
      className={cn(
        'flex w-full items-center rounded-[var(--r-sm)] px-2 py-2 text-left text-sm hover:bg-[var(--cream-0)]',
        className,
      )}
      onClick={onSelect}
      {...props}
    >
      {children}
    </button>
  );
}
